import { ReactNode } from "react"
import {
  Block,
  Card,
  CardBody,
  CardProps,
  LabelMedium,
  ListItem,
  ListItemLabel,
  mergeOverrides,
  ParagraphSmall,
  Tag,
  TagProps,
  useStyletron,
} from "@mezo-org/mezo-clay"

type ProductCardProps = Omit<CardProps, "children" | "overrides"> & {
  title: ReactNode
  description?: ReactNode
  endEnhancer?: () => ReactNode
  tagColor?: TagProps["color"]
  tagMessage?: ReactNode
  overrides?: CardProps["overrides"]
}

export default function ProductCard({
  title,
  description,
  endEnhancer,
  tagColor = "green",
  tagMessage,
  overrides,
  ...props
}: ProductCardProps) {
  const [_, theme] = useStyletron()

  return (
    <Card
      overrides={mergeOverrides(
        {
          Root: {
            style: {
              position: "relative",
            },
          },
          Contents: {
            style: {
              position: "unset",
            },
          },
          HeaderImage: {
            style: ({ $theme }) => ({
              width: "100%",
              borderRadius: $theme.sizing.scale800,
              padding: $theme.sizing.scale300,
              paddingBottom: 0,
            }),
          },
        },
        overrides,
      )}
      {...props}
    >
      {tagMessage && (
        <Tag
          color={tagColor}
          closeable={false}
          overrides={{
            Root: {
              style: ({ $theme }) => ({
                position: "absolute",
                top: $theme.sizing.scale600,
              }),
            },
          }}
        >
          {tagMessage}
        </Tag>
      )}

      <CardBody>
        <ListItem
          endEnhancer={endEnhancer}
          overrides={{
            Content: {
              style: { borderBottom: "none" },
            },
          }}
        >
          <ListItemLabel
            description={
              typeof description === "string" ? (
                <ParagraphSmall
                  color={theme.colors.contentSecondary}
                  whiteSpace="pre-line"
                >
                  {description}
                </ParagraphSmall>
              ) : (
                description
              )
            }
            overrides={{
              LabelContent: {
                component: Block,
              },
              LabelDescription: {
                component: Block,
              },
            }}
          >
            {typeof title === "string" ? (
              <LabelMedium>{title}</LabelMedium>
            ) : (
              title
            )}
          </ListItemLabel>
        </ListItem>
      </CardBody>
    </Card>
  )
}
